import Checkmark from 'components/Icons/Checkmark';
import Close from 'components/Icons/Close';
import Customize from 'components/Icons/Customize';
import { frameSizeConst } from 'constants/frameSize';
import React, { useMemo } from 'react';
import { useGlassesStore } from 'zus/glassesStore';
import { ColorTaxonomyType } from '../../constants/dataTypes';
import Button from 'components/Button/Button';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { parseAsString, useQueryState } from 'nuqs';
import { ColorSizeWithHex, ProductOptionsWithHex } from 'constants/dataDresdenTypes';

// Dynamically import UITooltip with ssr disabled
const UITooltip = dynamic(() => import('components/Others/UITooltip'), { ssr: false });

export default function ColorSizeFrameComponent({
	categorySlug,
	frameArmsOptions,
	frameColor,
	checkVariationOnColorPicker,
}: {
	categorySlug?: string;
	frameColor: string;
	frameArmsOptions: ProductOptionsWithHex;
	checkVariationOnColorPicker?: Function;
}) {


	const router = useRouter();
	const [armColorQuery, setArmColorQuery] = useQueryState('armColor');
	const [frameShapeQuery] = useQueryState('shape', parseAsString.withDefault('classic'));

	const frameSize = useGlassesStore((state) => state.frame_size);
	//const setFrameArmsColor = useGlassesStore((state) => state.setFrameArmsColor);
	//const setFrameColor = useGlassesStore((state) => state.setFrameColor);
	const setFrameColorDisplay = useGlassesStore((state) => state.setFrameColorDisplay);
	const setFrameSize = useGlassesStore((state) => state.setFrameSize);
	const setFrameArmsSize = useGlassesStore((state) => state.setFrameArmsSize);

	if (!categorySlug && !router.query.category) {
		throw new Error('Category slug is required');
	}
	const is_arm_customized = armColorQuery ? true : false; //useGlassesStore((state) => state.is_arm_customized);
	const setArmCustomized = useGlassesStore((state) => state.setArmCustomized);

	/*if(router.query.armColor && !is_arm_customized) {
		setArmCustomized(true);
	}*/

	const colorList = useMemo(() => {
		if (!frameArmsOptions) return null;
		let colors = frameArmsOptions.frame_options.find((item) => item.shape == frameShapeQuery.toLowerCase())?.color_size;
		//filter recycled-money
		if (router.locale !== 'ca') {
			colors = colors?.filter((item) => item.slug !== 'recycled-money');
		}
		return colors;
	}, [frameArmsOptions, frameShapeQuery]);

	const selectedColorName = useMemo(() => {
		if (!colorList) return '';
		const color = colorList.find((e) => e.slug == frameColor);
		if (color) {
			/*if(setFrameColorDisplay) {
				setFrameColorDisplay(color.name);
			}*/
			return color.name;
		}
		return '';
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [frameColor]);

	const armsColor = router.query.armColor;
	return (
		<div className="w-full select-none mb-6">
			<div className="overflow-hidden bg-body">
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-4">
						<h5 className="text-black font-medium text-[0.8rem] font-sans">
							Frame, <span className="text-[#959595]">{selectedColorName}</span>
						</h5>
						<div className="hide-scroll flex tsm:flex-wrap overflow-auto w-full gap-x-1.5 gap-y-2">
							{colorList &&
								colorList.map((e: ColorSizeWithHex, i: number) => (
									<UITooltip key={frameShapeQuery + i} id={`color-tooltip-${frameShapeQuery}-${i}`} delay="20" place="top" message={e.name} messageWrapperClassName={'text-black'} tooltipWrapperClassName={'!rounded-[4px] min-h-[40px] border !border-[#E2E2E2] border-solid !bg-white shadow-tooltipSecondary white-tip'}>
										<div
											key={frameShapeQuery + i}
											onClick={() => {
												if (frameColor !== e.slug) {
													const { category, armColor, lensColor, isUpdate } = router.query;
													const searchParams = new URLSearchParams();

													const categoryTarget = categorySlug ?? category;

													if (armColor) searchParams.append('armColor', armColor as string);
													if (lensColor) searchParams.append('lensColor', lensColor as string);
													if (frameShapeQuery !== 'classic') searchParams.append('shape', frameShapeQuery as string);
													if (isUpdate) searchParams.append('isUpdate', 'true');

													const queryString = searchParams.toString();
													const newUrl = `/shop/${categoryTarget}/${e.slug}${queryString ? `?${queryString}` : ''}`;

													// Use replace with shallow routing and scroll restoration disabled
													router.replace(newUrl, undefined, { 
														shallow: true,
														scroll: false 
													});
												}
											}}
											className={`flex items-center justify-center min-w-[30px] h-[30px] cursor-pointer rounded-full ${frameColor == e.slug
												? ''
												: 'border border-brand-grey200 p-[3px]'
												}`}>
											{frameColor == e.slug ?
												<div
													className={`w-full h-full rounded-full flex justify-center items-center`}
													style={{ background: `${e.hex}` }}>
													<Checkmark height={27} width={15} fill={e.name === "White" ? '#000' : '#FFF'} />
												</div>
												:
												<div
													className={`w-[16px] h-[16px] rounded-full`}
													style={{ background: `${e.hex}` }}>
												</div>
											}
										</div>
									</UITooltip>
								))}
						</div>
					</div>
					<div className="flex flex-col gap-4">
						<div className="font-medium tracking-wider text-[#616161] text-xs font-sans">
							Frame Size
						</div>
						<div className="flex gap-4">
							{/* <div className="flex justify-between w-[calc(100%-102px)] xsmx:w-[calc(100%-89px)]"> */}
							<div className="flex justify-between gap-2">
								{frameSizeConst.map((size) => {
									var check = true;
									if (checkVariationOnColorPicker) {
										var type;
										if (!is_arm_customized) {
											type = 'both'
										} else {
											type = 'Frame';
										}
										check = checkVariationOnColorPicker(type, size.short, frameColor);
									}

									return (
										<div
											key={size.id}
											onClick={() => {
												if (is_arm_customized) {
													setFrameSize(size.short);
												}
												else {
													setFrameArmsSize(size.short);
												}
											}}
											className={`${check ? '' : 'disable-size text-[#DEDEDE]'} cursor-pointer flex items-center justify-center bg-white border rounded-md w-[54px] h-[46px] p-1 xsmx:h-[53px] ${frameSize == size.short
												? 'border-black'
												: 'border-[#CACACA] hover:border-[#AEADAD]'
												} duration-300 transition-colors`}>
											<span className="font-sans font-medium text-sm text-center xsmx:text-xs">
												{size.short}
											</span>
											{check == false ? (<div className="hr"></div>) : <></>}
										</div>
									)

								})}
							</div>
							<a
								href="/our-glasses/frame-sizes"
								className=" flex items-center"
								target="_blank">
								<button className="text-sm xsmx:text-xs whitespace-nowrap">
									Find your size
								</button>
							</a>
						</div>
					</div>

					{!is_arm_customized && (
						<Button
							variant="text"
							onClick={() => {
								//setArmCustomized(true);
								setArmColorQuery(frameColor);
							}}
							className="text-sm font-medium flex border border-solid border-[#CACACA] gap-4 rounded-md bg-white w-[183px]"
						>

							<div className="flex gap-4 rounded-md px-4 py-4">
								<Customize />
								<p> {router.locale === "ca" ? "Customize" : "Customise"} Arms </p>
							</div>

						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
